import React, { useContext } from "react";
import { Controller, useForm } from "react-hook-form";
import { Button, Dialog } from "iuvity-ui-components";
import { CustomInput } from "../inputs/CustomInput";
import { VersionCreatorSchema } from "../../validations/schemas";
import { DialogProps } from "../dialog/CustomDialog";
import { Dropdown } from "iuvity-ui-components/dist/dropdown";
import { IM_CAMPAIGN_TYPES, REVALIDATE_FORM_PROPS } from "../../constants";
import { AppContext } from "../../context";
import { ErrorMessage } from "@hookform/error-message";

interface VersionCreatorProps {
  visible: boolean;
  setModalProps: React.Dispatch<
    React.SetStateAction<{
      visible: boolean;
      type: "CREATE" | "UPDATE" | null;
    }>
  >;
  update: boolean;
  createFn: Function;
  reloadFn: Function;
  auxModalController: React.Dispatch<React.SetStateAction<DialogProps>>;
}

export const versionCreatorDefaultValues = {
  number: "",
  name: "",
  description: "",
  status: "INACTIVE",
};

export const VersionCreator: React.FC<VersionCreatorProps> = ({
  visible,
  setModalProps,
  update,
  createFn,
  reloadFn,
  auxModalController,
}) => {
  const {
    t,
    appState: { user, selectedVersion },
  } = useContext(AppContext);

  const {
    control,
    getValues,
    reset,
    setValue,
    formState: { errors, isDirty, isValid },
  } = useForm({
    mode: "onChange",
    delayError: 1200,
    reValidateMode: "onChange",
    defaultValues: update && selectedVersion ? ({
      number: `${selectedVersion?.number}`,
      name: selectedVersion?.name,
      description: selectedVersion?.description || '',
      status: selectedVersion?.status,
    }) : versionCreatorDefaultValues
  });

  const onHideModal = () => {
    setModalProps((prev) => ({
      ...prev,
      visible: false,
    }));
    reset(versionCreatorDefaultValues);
  };

  const onCreateVersion = async () => {
    const formValues = getValues();
    onHideModal();
    const actionStatus = await createFn(update ? "UPDATE" : "CREATE", [
      {
        ...formValues,
        id: update && selectedVersion && selectedVersion.id,
        [update ? "updatedby" : "createdby"]: user?.name,
      },
    ]);
    if (actionStatus === "SUCCESS") {
      reset(versionCreatorDefaultValues);
      reloadFn();
    }
  };

  const handleModalProps = (isCancelButton?: boolean) => {
    auxModalController((prev) => ({
      ...prev,
      visible: true,
      title: isCancelButton
        ? t("Versions.cancelModalTitle")
        : t(`Versions.${update ? "save" : "create"}ModalTitle`),
      content: isCancelButton
        ? t("Versions.cancelModalContent")
        : t(`Versions.${update ? "save" : "create"}ModalContent`),
      onConfirm: isCancelButton ? onHideModal : onCreateVersion,
    }));
  };

  const renderFooter = () => {
    return (
      <div className="flex align-items-center justify-content-flex-end">
        <Button
          label={t("Versions.cancelBtnLabel")}
          onClick={
            isDirty && update ? () => handleModalProps(true) : onHideModal
          }
          className="mr-4"
          variant="p-button-link"
        />
        <Button
          label={
            update ? t("Versions.saveBtnLabel") : t("Versions.createBtnLabel")
          }
          variant="p-button"
          onClick={() => handleModalProps()}
          disabled={update ? !(isValid && isDirty) : !isValid}
        />
      </div>
    );
  };

  return (
    <Dialog
      className="dialog-form"
      header={
        update ? t("Versions.editVersionTitle") : t("Versions.newVersionTitle")
      }
      visible={visible}
      onHide={onHideModal}
      style={{ width: "25rem" }}
      footer={renderFooter}
    >
      <div className="form sm">
        {Object.keys(versionCreatorDefaultValues).map((key, idx, arr) => {
          return (
            <div key={key}>
              <Controller
                key={key}
                name={key as any}
                control={control}
                rules={
                  update && key === "description" && !selectedVersion?.createdby
                    ? (VersionCreatorSchema(t) as any)['descriptionvs2']
                    : (VersionCreatorSchema(t) as any)[key]
                }
                render={({ field }) => {
                  let extraProps = {
                    keyfilter: (VersionCreatorSchema(t) as any)[key]?.keyfilter,
                    maxLength: (VersionCreatorSchema(t) as any)[key]?.maxLength
                      ?.value,
                  };
                  if (key === "status") {
                    field.value = getValues("status") === "ACTIVE";
                    field.onChange = (e) => {
                      const newValue =
                        e.target.value === true ? "ACTIVE" : "INACTIVE";
                      setValue("status", newValue, REVALIDATE_FORM_PROPS);
                    };
                  }
                  return key === "type" ? (
                    // @ts-ignore
                    <Dropdown
                      id="im-type-dropdown"
                      className="dropdown mb-3"
                      label={t("Versions.typeLabel")}
                      placeholder={t("Versions.typePlaceholderLabel")}
                      options={IM_CAMPAIGN_TYPES(t)}
                      filter={false}
                      showClear={false}
                      style={{
                        zIndex: "2300 !important",
                      }}
                      {...field}
                    />
                  ) : (
                    <CustomInput
                      id={`input-${key}`}
                      className={
                        // @ts-ignore
                        errors[key]?.message
                          ? "p-invalid"
                          : idx !== arr.length - 1
                          ? "mb-3"
                          : ""
                      }
                      label={t(`Versions.${key}Label`)}
                      placeholder={t(`Versions.${key}Placeholder`)}
                      type={key === "status" ? "boolean" : "text"}
                      autoComplete="off"
                      {...field}
                      {...extraProps}
                    />
                  );
                }}
              />
              {key !== "status" && (
                <ErrorMessage
                  errors={errors}
                  name={key as any}
                  //@ts-ignore
                  render={({ message }) => (
                    <small
                      className="p-error block"
                      style={{ marginTop: "0.5rem", marginBottom: "1.5rem" }}
                    >
                      {message}
                    </small>
                  )}
                />
              )}
            </div>
          );
        })}
      </div>
    </Dialog>
  );
};
